import  { useState, useEffect,  useCallback } from 'react';
import type { Restaurant } from '../../Manager/restaurant.types';

interface UserActivity {
  type: 'click' | 'search' | 'scroll';
  payload: {
    restaurantId?: string;
    query?: string;
    sectionId?: string;
  };
  timestamp: number;
}

interface ExploreSection {
  id: string;
  title: string | ((area?: string) => string);
  endpoint: string | ((lat?: number, lng?: number) => string);
  isAIReplaceable: boolean;
  filterFn?: (restaurants: Restaurant[], area?: string) => Restaurant[];
}

export interface UserLocation {
  area: string | null;
  lat: number | null;
  lng: number | null;
  isLocationAllowed: boolean;
}

type LocationError =
  | 'NOT_SUPPORTED'
  | 'PERMISSION_DENIED'
  | 'POSITION_UNAVAILABLE'
  | 'TIMEOUT'
  | 'REVERSE_GEOCODE_FAILED'
  | null;

export const exploreSections: ExploreSection[] = [
  {
    id: "top-hidden-gems",
    title: (area?: string) => area ? `Top Hidden Gems near ${area}` : "Top Hidden Gems in Kenya",
    endpoint: (lat?: number, lng?: number) => {
      const params = new URLSearchParams();
      if (lat !== undefined && lng !== undefined) {
        params.append("lat", lat.toString());
        params.append("lng", lng.toString());
      }
      params.append("sort", "rating.desc");
      return `/api/restaurants?${params.toString()}`;
    },
    isAIReplaceable: false,
  },
  {
    id: "view-vibes",
    title: () => "Where the Vibe Is the View",
    endpoint: () => `/api/restaurants?tags=view,rooftop`,
    isAIReplaceable: true,
  },
  {
    id: "weekend-budget",
    title: () =>
      new Date().getDay() >= 5
        ? "Weekend Budget Picks"
        : "Tasty on a Budget",
    endpoint: () => `/api/restaurants?budgetLevel=$$`,
    isAIReplaceable: false,
  },
  {
    id: "cozy-cafes",
    title: () => "Cozy Cafés & Chill Spots",
    endpoint: () => `/api/restaurants?tags=cafe,cozy,chill`,
    isAIReplaceable: true,
  },
  {
    id: "dates-couples",
    title: () => Math.random() > 0.5 ? "Perfect for Dates & Couples" : "Perfect for a Night Out",
    endpoint: () => `/api/restaurants?experience=romantic,date-night`,
    isAIReplaceable: false,
  },
];

export const useUserLocation = () => {
  const [location, setLocation] = useState<UserLocation>({
    area: null,
    lat: null,
    lng: null,
    isLocationAllowed: false,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<LocationError>(null);

  useEffect(() => {
    if (!('geolocation' in navigator)) {
      setError('NOT_SUPPORTED');
      setLocation({
        area: null,
        lat: null,
        lng: null,
        isLocationAllowed: false,
      });
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;

        try {
          const { address, area } = await reverseGeocode(latitude, longitude);

          setLocation({
            area: area || address || 'Unknown location',
            lat: latitude,
            lng: longitude,
            isLocationAllowed: true,
          });
        } catch (err) {
          console.error('Reverse geocoding failed:', err);
          setError('REVERSE_GEOCODE_FAILED');

          setLocation({
            area: null,
            lat: latitude,
            lng: longitude,
            isLocationAllowed: true,
          });
        } finally {
          setLoading(false);
        }
      },
      (geoError) => {
        console.warn('Geolocation error:', geoError);

        let errorType: LocationError = 'POSITION_UNAVAILABLE';

        switch (geoError.code) {
          case geoError.PERMISSION_DENIED:
            errorType = 'PERMISSION_DENIED';
            break;
          case geoError.TIMEOUT:
            errorType = 'TIMEOUT';
            break;
        }

        setError(errorType);
        setLocation({
          area: null,
          lat: null,
          lng: null,
          isLocationAllowed: false,
        });
        setLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 8000,
        maximumAge: 0,
      }
    );
  }, []);

  return { location, loading, error };
};

export const formatAddressBadge = ( address: string,
  area: string
) => {
  const KEEP_SUFFIXES = ["Ave"];
  const REMOVE_SUFFIXES = ["Rd", "Road", "St", "Street", "Ln", "Lane"];

  const cleanStreet = (street: string) => {
    street = street.replace(/\d+/g, "").trim();
    const words = street.split(/\s+/);
    const cleaned = words.filter(
      w => KEEP_SUFFIXES.includes(w) || !REMOVE_SUFFIXES.includes(w)
    );
    return cleaned.join("");
  };

  const formatAreaCode = (area: string) =>
    area.replace(/\s+/g, "").slice(0, 3).toUpperCase();

  const streetPart = address.split(",")[0] || "";
  const street = cleanStreet(streetPart);
  const areaCode = formatAreaCode(area);

  return `${street} • ${areaCode}`;
};

export const useUserActivity = () => {
  const MAX_ACTIVITIES = 30;
  const STORAGE_KEY = 'userActivity';

  const getActivities = useCallback((): UserActivity[] => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }, []);

  const addActivity = useCallback((activity: Omit<UserActivity, 'timestamp'>) => {
    const activities = getActivities();
    const newActivity: UserActivity = {
      ...activity,
      timestamp: Date.now()
    };

    const updated = [newActivity, ...activities].slice(0, MAX_ACTIVITIES);
    
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    } catch (error) {
      console.error('Failed to save activity:', error);
    }
  }, [getActivities]);

  const trackClick = useCallback((restaurantId: string) => {
    addActivity({ type: 'click', payload: { restaurantId } });
  }, [addActivity]);

  const trackSearch = useCallback((query: string) => {
    addActivity({ type: 'search', payload: { query } });
  }, [addActivity]);

  // NEW: Get restaurant IDs from recent clicks for unauthenticated users
  const getRecentRestaurantIds = useCallback((): string[] => {
    const activities = getActivities();
    const restaurantIds = activities
      .filter(a => a.type === 'click' && a.payload.restaurantId)
      .map(a => a.payload.restaurantId!)
      .filter((id, index, self) => self.indexOf(id) === index) // unique only
      .slice(0, 10); // max 10 recent
    
    return restaurantIds;
  }, [getActivities]);

  // NEW: Clear activities after syncing with server
  const clearActivities = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear activities:', error);
    }
  }, []);

  return {
    activities: getActivities(),
    trackClick,
    trackSearch,
    getRecentRestaurantIds,
    clearActivities
  };
};

export const generateAISectionTitle = async (
  sectionId: string,
  defaultTitle: string,
  activities: UserActivity[]
): Promise<string> => {
  // Only generate AI titles if we have enough activity
  if (activities.length < 5) {
    return defaultTitle;
  }

  try {
    // Prepare activity summary
    const recentActivities = activities.slice(0, 10);
    const activitySummary = recentActivities.map(a => ({
      type: a.type,
      payload: a.payload
    }));

    const prompt = `Based on this user's recent restaurant browsing activity, suggest ONE catchy, engaging section title for a restaurant discovery page.

      User Activity Summary:
      ${JSON.stringify(activitySummary, null, 2)}

      Current section: ${defaultTitle}

      Requirements:
      - Keep it short (4-6 words max)
      - Make it exciting and personalized
      - Use casual, friendly language
      - Examples of good titles: "Where the Vibe Is the View", "Your Next Foodie Adventure", "Hidden Spots You'll Love"

      Respond with ONLY the title text, nothing else.`;

    const response = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: 'claude-sonnet-4-20250514',
        max_tokens: 1000,
        messages: [
          { role: 'user', content: prompt }
        ]
      })
    });

    const data = await response.json();
        
    if (data.content && data.content[0]?.text) {
      const aiTitle = data.content[0].text.trim().replace(/^["']|["']$/g, '');
      return aiTitle || defaultTitle;
    }
    
    return defaultTitle;
  } catch (error) {
    console.error('AI title generation failed:', error);
    return defaultTitle;
  }
};

export async function reverseGeocode(lat: number, lng: number) {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`
    );
    const data = await res.json();

    const address = data.address.road || data.address.pedestrian || data.address.house_number || data.display_name || "";

    // Dynamically pick city or town
    const city =
      data.address.city ||
      data.address.town ||
      data.address.village ||
      data.address.municipality ||
      "";

    // Dynamically pick area/suburb
    const area =
      data.address.suburb ||
      data.address.neighbourhood ||
      data.address.hamlet ||
      data.address.county ||
      data.address.state ||
      "";

    return { address, area, city };
  } catch (error) {
    console.error("Reverse geocode error:", error);
    return { address: "", area: "", city: "" };
  }
}

export  const generateUrl = (url: string) => {
    return `${url}?w=200&h=200&fit=crop&q=50`
  };

export  function extractLocationData(label: string) {
  if (!label) return { address: '', area: '', city: '' };

  const parts = label.split(',').map(p => p.trim()).filter(Boolean);

  const address = parts[0] || '';

  const nonCityPatterns = [
    /^\d+$/,            // postal codes
    /county/i,
    /state/i,
    /province/i,
    /region/i,
    /district/i,
    /division/i,
    /sublocation/i,
    /ward/i,
    /suburb/i,
    /location/i,
  ];

  let city = '';
  for (let i = parts.length - 1; i >= 0; i--) {
    const part = parts[i];
    if (!nonCityPatterns.some(rx => rx.test(part))) {
      city = part;
      break;
    }
  }

  let area = '';
  const cityIndex = parts.indexOf(city);
  if (cityIndex > 1) {
    for (let i = cityIndex - 1; i > 0; i--) {
      const part = parts[i];
      if (!nonCityPatterns.some(rx => rx.test(part))) {
        area = part;
        break;
      }
    }
    if (!area) area = parts[cityIndex - 1]; // fallback
  }

  return { address, area, city };
}

export  const slugify = (text: string) => {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
};